import { useEffect, useState } from "react";
import { CDSButton } from "@ciscodesignsystems/cds-react-button";
import { CDSCard } from "@ciscodesignsystems/cds-react-card";
import { CDSContainer } from "@ciscodesignsystems/cds-react-container";
import { CDSFlex } from "@ciscodesignsystems/cds-react-flex";
import { useNavigate } from "react-router-dom";
import { DESIGN_API, ON_BOARD, REVIEW_SPEC } from "../app/paths";

const quickActions = [
  {
    title: "Onboard API",
    description:
      "Capture service details, gateway needs, environments, and owners before onboarding work starts.",
    ctaLabel: "Start onboarding",
    destination: ON_BOARD,
  },
  {
    title: "Design API",
    description:
      "Define resources, operations, auth, pagination, and error handling before backend implementation.",
    ctaLabel: "Start design",
    destination: DESIGN_API,
  },
  {
    title: "Review Spec",
    description:
      "Upload or paste JSON / YAML and get governance feedback with findings, locations, and fixes.",
    ctaLabel: "Start review",
    destination: REVIEW_SPEC,
  },
];

const designApiStorageKey = "uag.designApiPage";
const reviewSpecStorageKey = "uag.reviewSpecPage";
const onboardDraftStorageKey = "uag.onboard.intake-draft";
const browserStorageNames = ["sessionStorage", "localStorage"];

const defaultPlatformSummary = [
  { label: "Recent reviews", value: "0" },
  { label: "Saved drafts", value: "0" },
  { label: "Open findings", value: "0" },
];

const summaryDestinations = {
  "Recent reviews": REVIEW_SPEC,
  "Saved drafts": DESIGN_API,
  "Open findings": REVIEW_SPEC,
};

const suggestions = [
  "Start with Design API when the contract is still evolving.",
  "Use Review Spec for an existing OpenAPI file or generated draft.",
  "Review open findings before publishing or handing off to backend teams.",
];
const browserWindow = globalThis.window;

const isObject = (value) => Boolean(value) && typeof value === "object" && !Array.isArray(value);

const parseStoredState = (storedValue) => {
  if (!storedValue) {
    return null;
  }

  try {
    const parsedValue = JSON.parse(storedValue);

    if (!isObject(parsedValue)) {
      return null;
    }

    return parsedValue;
  } catch (error) {
    return null;
  }
};

export const getStoredState = (windowObject, key) => {
  if (!windowObject) {
    return null;
  }

  for (const storageName of browserStorageNames) {
    try {
      const storage = windowObject[storageName];
      const parsedValue = parseStoredState(storage?.getItem(key));

      if (parsedValue) {
        return parsedValue;
      }
    } catch (error) {
      continue;
    }
  }

  return null;
};

export const getOpenFindingsCount = (reviewState) => {
  if (!isObject(reviewState)) {
    return 0;
  }

  const reviewSummary = isObject(reviewState.reviewSummary) ? reviewState.reviewSummary : {};
  const summaryTotal = Number(reviewSummary.total || 0);

  if (summaryTotal > 0) {
    return summaryTotal;
  }

  if (Array.isArray(reviewState.findings)) {
    return reviewState.findings.length;
  }

  return 0;
};

export const getPlatformSummary = (windowObject) => {
  const reviewState = getStoredState(windowObject, reviewSpecStorageKey);
  const designState = getStoredState(windowObject, designApiStorageKey);
  const onboardState = getStoredState(windowObject, onboardDraftStorageKey);
  const openFindings = getOpenFindingsCount(reviewState);
  const recentReviews = openFindings > 0 ? 1 : 0;
  const activeDrafts = [designState, onboardState].filter(isObject).length;

  return [
    { label: "Recent reviews", value: String(recentReviews) },
    { label: "Saved drafts", value: String(activeDrafts) },
    { label: "Open findings", value: String(openFindings) },
  ];
};

export const getInitialPlatformSummary = (windowObject) => {
  if (!windowObject) {
    return defaultPlatformSummary;
  }

  return getPlatformSummary(windowObject);
};

export const registerPlatformSummarySync = (windowObject, setPlatformSummary) => {
  if (!windowObject) {
    return undefined;
  }

  const syncPlatformSummary = () => {
    setPlatformSummary(getPlatformSummary(windowObject));
  };

  windowObject.addEventListener("storage", syncPlatformSummary);

  return () => {
    windowObject.removeEventListener("storage", syncPlatformSummary);
  };
};

export const handlePlatformSummaryNavigation = (item, navigate) => {
  const itemValue = Number(item.value || 0);

  if (itemValue <= 0) {
    return;
  }

  navigate(summaryDestinations[item.label]);
};

const OverviewPage = () => {
  const navigate = useNavigate();
  const [platformSummary, setPlatformSummary] = useState(() => getInitialPlatformSummary(browserWindow));

  useEffect(() => {
    return registerPlatformSummarySync(browserWindow, setPlatformSummary);
  }, []);

  return (
    <CDSContainer className="uag-overview-page" aria-labelledby="overview-title">
      <CDSFlex className="uag-overview-header" direction="vertical" gap={2}>
        <h1 id="overview-title">Overview</h1>
        <p className="uag-overview-subtitle">
          Start onboarding, shape API designs, and review governance findings from one workspace.
        </p>
      </CDSFlex>

      <CDSCard className="uag-overview-hero" aria-labelledby="overview-hero-title">
        <div className="uag-overview-hero-accent" aria-hidden="true" />
        <CDSFlex direction="vertical" gap={10}>
          <h2 id="overview-hero-title">Build or review API specifications in one place</h2>
          <p>
            Start with API design, generate an OpenAPI specification, then review governance
            findings.
          </p>
        </CDSFlex>
      </CDSCard>

      <CDSFlex className="uag-overview-actions" gap={24} aria-label="Overview actions">
        {quickActions.map((action) => (
          <CDSCard key={action.title} className="uag-overview-card uag-overview-action-card">
            <CDSFlex className="uag-overview-card-body" direction="vertical" gap={24}>
              <CDSFlex direction="vertical" gap={8}>
                <h2>{action.title}</h2>
                <p>{action.description}</p>
              </CDSFlex>
              <CDSButton onClick={() => navigate(action.destination)}>{action.ctaLabel}</CDSButton>
            </CDSFlex>
          </CDSCard>
        ))}
      </CDSFlex>

      <CDSFlex className="uag-overview-insights" gap={24} aria-label="Platform insights">
        <CDSCard className="uag-overview-card uag-overview-summary-card">
          <CDSFlex className="uag-overview-card-body" direction="vertical" gap={24}>
            <h2>Platform Summary</h2>
            <ul className="uag-overview-stats" aria-label="Platform summary metrics">
              {platformSummary.map((item) => (
                <li key={item.label} className="uag-overview-stat">
                  {Number(item.value) > 0 ? (
                    <button
                      type="button"
                      className="uag-overview-stat-button"
                      onClick={() => handlePlatformSummaryNavigation(item, navigate)}
                      aria-label={`${item.label}: ${item.value}`}
                    >
                      <strong>{item.value}</strong>
                    </button>
                  ) : (
                    <strong>{item.value}</strong>
                  )}
                  <span>{item.label}</span>
                </li>
              ))}
            </ul>
          </CDSFlex>
        </CDSCard>

        <CDSCard className="uag-overview-card uag-overview-suggestions-card">
          <CDSFlex className="uag-overview-card-body" direction="vertical" gap={24}>
            <h2>Suggested next steps</h2>
            <ul className="uag-overview-suggestions">
              {suggestions.map((suggestion) => (
                <li key={suggestion}>{suggestion}</li>
              ))}
            </ul>
          </CDSFlex>
        </CDSCard>
      </CDSFlex>
    </CDSContainer>
  );
};

export default OverviewPage;
